import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db, storage } from "../services/firebase";
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  updateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  deleteUser,
  signInWithPhoneNumber,
} from "firebase/auth";

import { collection, doc, getDoc, addDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import "react-toastify/dist/ReactToastify.css";
import { Bounce, toast } from "react-toastify";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        const userDoc = await getUserData(user.uid);
        setUserData(userDoc);
      } else {
        setUserData(null);
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const getUserData = async (uid) => {
    const userDoc = await getDoc(doc(db, "users", uid));
    return userDoc.exists() ? userDoc.data() : null;
  };

  const addProduct = async (productData, imageFiles) => {
    try {
      const imageUrls = [];

      for (let i = 0; i < imageFiles.length; i++) {
        const imageFile = imageFiles[i];

        const storageRef = ref(storage, `product_images/${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log(`Upload is ${progress}% done`);
            },
            (error) => {
              console.error("Error uploading image:", error);
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              // console.log("Image uploaded, download URL:", downloadURL);
              imageUrls.push(downloadURL);
              resolve();
              // notifyAdd();
            },
          );
        });
      }

      await addDoc(collection(db, "products"), { ...productData, imageUrls });
    } catch (error) {
      console.error("Error adding product:", error);
    }
  };

  const addAds = async (adsData, imageFiles) => {
    try {
      const imageUrls = [];

      for (let i = 0; i < imageFiles.length; i++) {
        const imageFile = imageFiles[i];

        const storageRef = ref(storage, `ads_images/${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log(`Upload is ${progress}% done`);
            },
            (error) => {
              console.error("Error uploading image:", error);
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log("Image uploaded, download URL:", downloadURL);
              imageUrls.push(downloadURL);
              resolve();
            },
          );
        });
      }

      await addDoc(collection(db, "ads"), { ...adsData, imageUrls });
    } catch (error) {
      console.error("Error adding ads:", error);
    }
  };
  const addBanner = async (bannerData, imageFiles) => {
    try {
      const imageUrls = [];

      for (let i = 0; i < imageFiles.length; i++) {
        const imageFile = imageFiles[i];

        const storageRef = ref(storage, `banner_images/${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log(`Upload is ${progress}% done`);
            },
            (error) => {
              console.error("Error uploading image:", error);
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log("Image uploaded, download URL:", downloadURL);
              imageUrls.push(downloadURL);
              resolve();
            },
          );
        });
      }

      await addDoc(collection(db, "banners"), { ...bannerData, imageUrls });
    } catch (error) {
      console.error("Error adding banner:", error);
    }
  };

  const addCategory = async (categoryData, imageFiles) => {
    try {
      const imageUrls = [];

      for (let i = 0; i < imageFiles.length; i++) {
        const imageFile = imageFiles[i];

        const storageRef = ref(storage, `category_images/${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log(`Upload is ${progress}% done`);
            },
            (error) => {
              console.error("Error uploading image:", error);
              reject(error);
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log("Image uploaded, download URL:", downloadURL);
              imageUrls.push(downloadURL);
              resolve();
            },
          );
        });
      }

      await addDoc(collection(db, "categories"), {
        ...categoryData,
        imageUrls,
      });
    } catch (error) {
      console.error("Error adding Category:", error);
    }
  };

  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signin = async (email, password, userName, number) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;
      const userId = user.uid;

      await updateProfile(user, {
        displayName: userName,
        phoneNumber: number,
      });

      const userProfile = {
        userName,
        number,
        userId,
        email,
      };

      await sendEmailVerification(user);

      await setDoc(doc(db, "users", user.uid), userProfile);
      console.log("User document successfully written!");

      return userCredential;
    } catch (error) {
      console.error("Error during signup:", error);

      if (error.code === "auth/email-already-in-use") {
        throw new Error("This email is already in use.");
      } else if (error.code === "auth/invalid-email") {
        throw new Error("Invalid email address.");
      } else if (error.code === "auth/weak-password") {
        throw new Error("Password is too weak.");
      } else {
        throw new Error("Failed to signup. Please try again later.");
      }
    }
  };

  // Add phone auth methods to your context
  const signInWithPhone = async (phoneNumber, recaptchaVerifier) => {
    return await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
  };

  const verifyPhoneCode = async (confirmationResult, code) => {
    return await confirmationResult.confirm(code);
  };

  const logout = () => {
    return signOut(auth);
  };

  const updateUserPassword = async (currentPassword, newPassword) => {
    try {
      const user = auth.currentUser;

      if (user) {
        try {
          await updatePassword(user, newPassword);
        } catch (error) {
          if (error.code === "auth/requires-recent-login") {
            await reauthenticateUser(user.email, currentPassword);
            await updatePassword(user, newPassword);
          } else {
            throw error;
          }
        }
      } else {
        console.error("No user is currently signed in.");
      }
    } catch (error) {
      console.error("Error updating password:", error.message);
    }
  };

  const reauthenticateUser = async (email, password) => {
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(email, password);

      await reauthenticateWithCredential(user, credential);
    } catch (error) {
      console.error("Reauthentication failed:", error.message);
    }
  };

  const updateUserEmail = async (newEmail, password) => {
    try {
      const user = auth.currentUser;

      if (user) {
        try {
          await updateEmail(user, newEmail);
          await sendEmailVerification(user);
          toast.success("Verification email sent to new address.", {
            position: "top-right",
            autoClose: 1000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
          });
        } catch (error) {
          if (error.code === "auth/requires-recent-login") {
            console.log(
              "Reauthentication required, attempting to reauthenticate...",
            );
            await reauthenticateUser(user.email, password);
            await updateEmail(user, newEmail);
            await sendEmailVerification(user);
          } else {
            throw error;
          }
        }
      } else {
        console.error("No user is currently signed in.");
      }
    } catch (error) {
      console.error("Error updating email:", error.message);
    }
  };

  const deleteAccount = async (email, password) => {
    try {
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(currentUser, credential);

      await deleteUser(currentUser);

      toast.success("Your account has been successfully deleted.", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } catch (error) {
      console.error("Error deleting account:", error);
      if (error.code === "auth/requires-recent-login") {
        toast.warning("Please reauthenticate to delete your account.", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else {
        toast.error("Failed to delete your account. Please try again later.", {
          position: "top-right",
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    }
  };

  const value = {
    currentUser,
    userData,
    loading,
    login,
    signin,
    logout,
    addProduct,
    addAds,
    addBanner,
    addCategory,
    updateUserPassword,
    updateUserEmail,
    deleteAccount,

    signInWithPhone,
    verifyPhoneCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
